import { defineRouter } from '#q-app'
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from 'vue-router'
import routes from './routes'
import { installNavigationErrorHandler } from 'src/lib/navigationRecovery'
import { browserNavigationErrorDeps } from 'src/lib/navigationErrorHandler'
import { installGlobalScope } from 'src/stores/globalScope'
import { beginBusy } from 'src/core/busy'
import { needsSessionBoot } from 'src/core/viewer'
import { useAuthStore } from 'src/stores/auth'

export default defineRouter(function () {
  const createHistory = import.meta.env.QUASAR_SERVER
    ? createMemoryHistory
    : import.meta.env.QUASAR_VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,
    history: createHistory(import.meta.env.QUASAR_VUE_ROUTER_BASE),
  })

  // A lazy route whose chunk no longer exists (the tab has been open across a
  // deploy) otherwise rejects into nothing: the route never changes and the
  // app looks frozen. See src/lib/navigationRecovery.ts.
  if (!import.meta.env.QUASAR_SERVER) {
    installNavigationErrorHandler(Router, browserNavigationErrorDeps())
  }

  // A pre-auth page has no token. An in-app link from a public page into a
  // tool goes through the auth boot as a full page load, which signs the
  // viewer in, instead of rendering the tool with nothing behind it (#123:
  // public pages render in the main layout, whose links lead into the tools).
  Router.beforeEach((to) => {
    if (!needsSessionBoot(useAuthStore(), to.path)) return true
    window.location.assign(Router.resolve(to).href)
    return false
  })

  // The shell-level competition/season scope rides the URL across every
  // navigation (ui-conventions.md #97) — see src/stores/globalScope.ts.
  installGlobalScope(Router)

  // The header's loading stripe (src/core/busy.ts) also runs while a
  // navigation resolves — a lazy route's chunk loading, its guards.
  let endNavigation: (() => void) | null = null
  const settleNavigation = () => {
    endNavigation?.()
    endNavigation = null
  }
  Router.beforeEach(() => {
    settleNavigation()
    endNavigation = beginBusy()
  })
  Router.afterEach(settleNavigation)
  Router.onError(settleNavigation)

  return Router
})
